// src/pages/Room2.jsx
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Sparkles, Wifi, Coffee, Wind, Droplets, Leaf, Users, ShieldCheck, UtensilsCrossed, Bath } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';

// Images (your River Resort ones)
import deluxeImg from '../assets/River/18.jpeg';
import seaViewImg from '../assets/River/45.jpeg';
import heroBg from '../assets/River/2.jpeg';
import roomSlide1 from '../assets/River/19.jpeg';
import roomSlide2 from '../assets/River/45.jpeg';
import roomSlide3 from '../assets/River/22.jpeg';
import roomSlide4 from '../assets/River/46.jpeg';

// Animation variants (same as original)
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.2, delayChildren: 0.3 }
    }
};

const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.94 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, ease: "easeOut" } }
};

export default function Room2() {
    const { t } = useTranslation('rooms2');
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [activeCategory, setActiveCategory] = useState('All');

    // Dynamically build rooms from translation keys
    const roomsList = [
        {
            id: 1,
            nameKey: 'deluxeRoomName',
            categoryKey: 'deluxeRoomCategory',
            descKey: 'deluxeRoomDesc',
            category: 'Deluxe Rooms',
            image: deluxeImg,
            size: '55 sqm',
            occupancy: 2,
            amenities: ['kingSizeBed', 'workDesk', 'rainShower', 'teaStation'],
            features: ['riverView', 'naturalLighting', 'gardenView']
        },
        {
            id: 2,
            nameKey: 'riverViewDeluxeName',
            categoryKey: 'riverViewDeluxeCategory',
            descKey: 'riverViewDeluxeDesc',
            category: 'Deluxe Room with River View',
            image: seaViewImg,
            size: '45 sqm',
            occupancy: 2,
            amenities: ['kingSizeBed', 'privateTerrace', 'rainShower', 'binoculars'],
            features: ['riverView', 'sunriseBalcony', 'soundOfRiver']
        }
    ];

    // Categories for filter
    const categories = [
        { key: 'All', label: t('filterAll') },
        { key: 'Deluxe Rooms', label: t('filterDeluxeRooms') },
        { key: 'Deluxe Room with River View', label: t('filterRiverViewDeluxe') }
    ];

    const filteredRooms = roomsList.filter(room =>
        activeCategory === 'All' || room.category === activeCategory
    );

    const detailPath = (room) => {
        if (room.category === 'Deluxe Room with River View') return '/river/rooms/deluxe-river-view-room';
        if (room.category === 'Deluxe Rooms') return '/river/rooms/deluxe-room';
        return '/river/rooms';
    };

    // Room gallery slider images
    const roomGalleryImages = [
        roomSlide1,
        roomSlide2,
        roomSlide3,
        roomSlide4
    ];

    const roomSlideCategoryKeys = [
        'deluxeRoomCategory',
        'riverViewDeluxeCategory',
        'deluxeRoomCategory',
        'riverViewDeluxeCategory'
    ];

    const roomSlides = roomGalleryImages.map((image, idx) => {
        const categoryKey = roomSlideCategoryKeys[idx % roomSlideCategoryKeys.length];
        const baseRoom = roomsList.find(r => r.categoryKey === categoryKey) || roomsList[idx % roomsList.length];
        return {
            image,
            nameKey: baseRoom.nameKey,
            categoryKey: baseRoom.categoryKey,
            path: detailPath(baseRoom)
        };
    });

    const roomSliderSettings = {
        dots: false,
        infinite: true,
        speed: 4000,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 0,
        cssEase: "linear",
        arrows: false,
        pauseOnHover: false,
        responsive: [
            { breakpoint: 1280, settings: { slidesToShow: 3 } },
            { breakpoint: 1024, settings: { slidesToShow: 2 } },
            { breakpoint: 768, settings: { slidesToShow: 2 } }
        ]
    };

    const smRoomSliderSettings = {
        dots: false,
        infinite: true,
        speed: 3200,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 0,
        cssEase: "linear",
        arrows: false,
        pauseOnHover: false
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-green-50">
            {/* Hero Section */}
            <div className="relative h-[70vh] md:h-[80vh] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${heroBg})` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-800/70 to-transparent" />
                </div>

                <div className="relative h-full flex items-center">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
                        <div className="max-w-2xl">
                            <div className="flex items-center space-x-2 mb-6">
                                <Leaf className="w-8 h-8 text-amber-400" />
                                <span className="text-amber-300 tracking-widest text-sm font-light">
                                    {t('resortName')}
                                </span>
                            </div>
                            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-6 leading-tight">
                                {t('heroTitle')}
                            </h1>
                            <p className="text-lg md:text-xl text-green-50 font-light leading-relaxed mb-8">
                                {t('heroSubtitle')}
                            </p>

                        </div>
                    </div>
                </div>
            </div>

            {/* Filter bar */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm shadow-sm z-10 border-b border-green-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-center md:justify-start">
                        <div className="flex space-x-3 overflow-x-auto pb-1">
                            {categories.map((cat) => (
                                <button
                                    key={cat.key}
                                    onClick={() => setActiveCategory(cat.key)}
                                    className={`
                                        px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300
                                        ${activeCategory === cat.key
                                            ? 'bg-green-700 text-white shadow-md'
                                            : 'text-gray-600 hover:bg-green-100 hover:text-green-800'}
                                    `}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Rooms Grid */}
            <div className="px-6 lg:px-8 py-16 bg-[#1F1F1F]">
                {filteredRooms.length === 0 ? (
                    <div className="text-center py-20 text-gray-500">
                        {t('noRoomsFound')}
                    </div>
                ) : (
                    <motion.div
                        className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 lg:gap-12"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        key={activeCategory}
                    >
                        {filteredRooms.map((room) => (
                            <motion.div
                                key={room.id}
                                variants={cardVariants}
                                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="relative h-64 md:h-72 lg:h-80 overflow-hidden">
                                    <img
                                        src={room.image}
                                        alt={t(room.nameKey)}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute top-4 left-4 bg-amber-500/95 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-lg">
                                        <span className="text-white text-xs font-medium tracking-wide">
                                            {t(room.categoryKey)}
                                        </span>
                                    </div>
                                </div>

                                <div className="p-6 md:p-8 lg:p-10">
                                    <h3 className="text-2xl md:text-3xl font-light text-gray-900 mb-3">
                                        {t(room.nameKey)}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed mb-6">
                                        {t(room.descKey)}
                                    </p>

                                    <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-gray-100">
                                        <div className="text-center">
                                            <div className="text-xl md:text-2xl font-light text-green-700">{room.size}</div>
                                            <div className="text-xs text-gray-500 mt-1">{t('roomSize')}</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="flex items-center justify-center space-x-1 text-green-700">
                                                <Users className="w-5 h-5" />
                                                <span className="text-xl md:text-2xl font-light">{room.occupancy}</span>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">{t('guests')}</div>
                                        </div>

                                    </div>

                                    <div className="space-y-6 mb-8">
                                        <div>
                                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                                {t('roomAmenities')}
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {room.amenities.map((a) => (
                                                    <span key={a} className="inline-flex items-center space-x-1 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-sm">
                                                        <Sparkles className="w-3.5 h-3.5" />
                                                        <span>{t(a)}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                                {t('specialFeatures')}
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {room.features.map((f) => (
                                                    <span key={f} className="inline-flex items-center space-x-1 bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full text-sm">
                                                        <Leaf className="w-3.5 h-3.5" />
                                                        <span>{t(f)}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <button
                                            onClick={() => navigate('/river/search')}
                                            className="flex-1 bg-green-700 text-white px-6 py-3.5 rounded-full font-medium hover:bg-green-800 transition-all shadow-md hover:shadow-lg"
                                        >
                                            {t('bookNow')}
                                        </button>
                                        <button
                                            onClick={() => navigate(detailPath(room))}
                                            className="flex-1 border-2 border-green-700 text-green-700 px-6 py-3.5 rounded-full font-medium hover:bg-green-50 transition-all"
                                        >
                                            {t('viewDetails')}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </div>

            {/* All Rooms Include */}
            <div className="bg-gradient-to-b from-green-50 to-white py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-5xl font-light text-gray-900 mb-4">
                            {t('allRoomsInclude')}
                        </h2>
                        <p className="text-xl text-gray-600 font-light">
                            {t('essentialComforts')}
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { icon: Wifi, label: 'Free Wifi' },
                            { icon: Wind, label: 'Air Conditioning' },
                            { icon: Droplets, label: 'Hot water' },
                            { icon: Coffee, label: 'Mini fridge' },
                            { icon: ShieldCheck, label: 'Safety deposit box' },
                            { icon: UtensilsCrossed, label: 'Electric kettle' },
                            { icon: Sparkles, label: 'Daily housekeeping' },
                            { icon: Bath, label: 'Hair dryer' }
                        ].map((item, index) => (
                            <div key={index} className="text-center group">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:bg-green-700 mb-4">
                                    <item.icon className="w-7 h-7 text-green-700 group-hover:text-white transition-colors duration-300" />
                                </div>
                                <p className="text-gray-700 font-medium">{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="relative py-24 overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.pexels.com/photos/3771640/pexels-photo-3771640.jpeg?auto=compress&cs=tinysrgb&w=1600')"
                    }}
                >
                    <div className="absolute inset-0 bg-green-900/85"></div>
                </div>

                <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <Leaf className="w-12 h-12 text-amber-400 mx-auto mb-6" />
                    <h2 className="text-4xl lg:text-5xl font-light text-white mb-6">
                        {t('beginHealing')}
                    </h2>
                    <p className="text-xl text-green-100 mb-10 font-light leading-relaxed max-w-2xl mx-auto">
                        {t('beginHealingDesc')}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <button
                            onClick={() => navigate('/river/search')}
                            className="w-full sm:w-auto bg-white text-green-800 px-8 py-4 rounded-full font-semibold hover:bg-amber-50 transition-all duration-300 shadow-xl hover:shadow-2xl"
                        >
                            {t('reserveRoom')}
                        </button>
                        <a
                            href="https://wa.me/94777382466"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full sm:w-auto border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300 text-center"
                        >
                            {t('speakAdvisor')}
                        </a>
                    </div>
                </div>
            </div>

            {/* Room image sliders – mobile */}
            <section className="block sm:hidden w-full py-10 bg-[#1F1F1F] overflow-hidden">
                <div className="max-w-md mx-auto px-4">
                    <Slider {...smRoomSliderSettings}>
                        {roomSlides.map((slide, index) => (
                            <div key={index} className="px-3">
                                <div className="overflow-hidden rounded-2xl bg-white/5 border border-white/10 shadow-lg">
                                    <div className="relative h-64">
                                        <img
                                            src={slide.image}
                                            alt={t(slide.nameKey)}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                                        <div className="absolute bottom-4 left-4 right-4 text-white">
                                            <p className="text-xs uppercase tracking-wide text-amber-300 mb-1">
                                                {t(slide.categoryKey)}
                                            </p>
                                            <h3 className="text-xl font-semibold leading-tight">
                                                {t(slide.nameKey)}
                                            </h3>
                                        </div>
                                    </div>
                                    <button
                                        className="w-full px-4 py-3 bg-amber-500 text-white font-semibold uppercase tracking-wide text-sm hover:bg-amber-400 transition-colors"
                                        onClick={() => navigate(slide.path)}
                                    >
                                        {t('viewRoom')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </Slider>
                </div>
            </section>

            {/* Room image sliders – desktop */}
            <section className="hidden sm:block w-full py-14 bg-[#1F1F1F] overflow-hidden">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-2xl md:text-3xl font-semibold text-white">
                            {t('exploreOurRooms')}
                        </h3>
                    </div>
                    <Slider {...roomSliderSettings}>
                        {roomSlides.map((slide, index) => (
                            <div key={index} className="px-3">
                                <div className="overflow-hidden rounded-2xl bg-white/5 border border-white/10 shadow-xl group">
                                    <div className="relative h-72">
                                        <img
                                            src={slide.image}
                                            alt={t(slide.nameKey)}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent" />
                                        <div className="absolute bottom-4 left-5 right-5 text-white flex items-center justify-between">
                                            <div>
                                                <p className="text-xs uppercase tracking-wide text-amber-300 mb-1">
                                                    {t(slide.categoryKey)}
                                                </p>
                                                <h3 className="text-2xl font-semibold leading-tight">
                                                    {t(slide.nameKey)}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-5 flex items-center justify-between">
                                        <button
                                            className="px-4 py-2 rounded-full bg-amber-500 text-white text-sm font-semibold uppercase tracking-wide hover:bg-amber-400 transition-colors"
                                            onClick={() => navigate(slide.path)}
                                        >
                                            {t('viewDetails')}
                                        </button>
                                        <button
                                            className="text-sm text-amber-200 hover:text-white transition-colors"
                                            onClick={() => navigate('/river/search')}
                                        >
                                            {t('bookNow')} →
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Slider>
                </div>
            </section>
        </div>
    );
}